import { JSONSchema7 } from 'json-schema';
import { types as commonTypes } from '@sitebuilder/common';
import Page from './Page';

const schema: JSONSchema7 = {
    title: 'Page',
    type: 'object',
    required: ['pageName'],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        pageName: {
            type: 'string',
            title: 'Page Name',
            description: 'Add unique name for page',
            default: 'page',
        },
        pageSize: {
            type: 'string',
            title: 'Page Size',
            enum: ['letter', 'A4', 'legal', 'Custom'],
            default: 'letter',
        },
        pageOrientation: {
            type: 'string',
            title: 'Orientation',
            enum: ['portrait', 'landscape'],
            default: 'portrait',
        },
        pageMargin: {
            type: 'object',
            title: 'Page Margin',
            properties: {
                top: {
                    title: 'Top',
                    type: 'number',
                    default: 0.4,
                },
                left: {
                    title: 'Left',
                    type: 'number',
                    default: 0.4,
                },
                bottom: {
                    title: 'Bottom',
                    type: 'number',
                    default: 0.4,
                },
                right: {
                    title: 'Right',
                    type: 'number',
                    default: 0.4,
                },
            },
        },
        pagePadding: {
            type: 'object',
            title: 'Page Padding',
            properties: {
                top: {
                    title: 'Top',
                    type: 'number',
                    default: 0,
                },
                left: {
                    title: 'Left',
                    type: 'number',
                    default: 0,
                },
                bottom: {
                    title: 'Bottom',
                    type: 'number',
                    default: 0,
                },
                right: {
                    title: 'Right',
                    type: 'number',
                    default: 0,
                },
            },
        },
        pageBleed: {
            type: 'object',
            title: 'Page Bleed',
            properties: {
                top: {
                    title: 'Top',
                    type: 'number',
                    default: 0,
                },
                left: {
                    title: 'Left',
                    type: 'number',
                    default: 0,
                },
                bottom: {
                    title: 'Bottom',
                    type: 'number',
                    default: 0,
                },
                right: {
                    title: 'Right',
                    type: 'number',
                    default: 0,
                },
            },
        },
        overflowOption: {
            type: 'string',
            title: 'Overflow Option',
            enum: ['hidden', 'noContinue', 'continue'],
            enumNames: [
                'Hide Overflow (No Continuation)',
                'Move Overflow to Next Page (No Continuation)',
                'Move Overflow to Next Page (Continue with Chained Flow)',
            ],
            default: 'hidden',
        },
        includeHeader: {
            type: 'boolean',
            title: 'Include Header',
            default: false,
        },
        includeFooter: {
            type: 'boolean',
            title: 'Include Footer',
            default: false,
        },
        pageLayout: {
            type: 'string',
            title: 'Page Layout',
            enum: ['None', 'TwoColumn'],
            default: 'None',
        },
    },
    dependencies: {
        pageLayout: {
            oneOf: [
                {
                    properties: {
                        pageLayout: { enum: ['TwoColumn'] },
                        leftColumnOptions: {
                            type: 'object',
                            title: 'Left Column Options',
                            properties: {
                                width: {
                                    title: 'Width',
                                    type: 'string',
                                    default: '50%',
                                },
                                padding: {
                                    title: 'Padding',
                                    type: 'string',
                                    default: '10px',
                                },
                                border: {
                                    title: 'Border',
                                    type: 'string',
                                    default: '1px dashed lightgrey',
                                },
                            },
                        },
                        rightColumnOptions: {
                            type: 'object',
                            title: 'Right Column Options',
                            properties: {
                                width: {
                                    title: 'Width',
                                    type: 'string',
                                    default: '50%',
                                },
                                padding: {
                                    title: 'Padding',
                                    type: 'string',
                                    default: '10px',
                                },
                                border: {
                                    title: 'Border',
                                    type: 'string',
                                    default: '1px dashed lightgrey',
                                },
                            },
                        },
                    },
                },
            ],
        },
        pageSize: {
            oneOf: [
                {
                    properties: {
                        pageSize: { enum: ['Custom'] },
                        pageWidth: {
                            title: 'Width',
                            type: 'number',
                            default: 8.5,
                        },
                        pageHeight: {
                            title: 'Height',
                            type: 'number',
                            default: 11,
                        },
                    },
                },
            ],
        },
        overflowOption: {
            oneOf: [
                {
                    properties: {
                        overflowOption: { enum: ['continue'] },
                        chainFlowName: {
                            title: 'Chained Flow Name',
                            type: 'string',
                            description: 'Name to continue overflow',
                        },
                        isSource: {
                            title: 'Is Source',
                            type: 'boolean',
                            default: false,
                            description: 'If this page is source for overflow',
                        },
                        isTarget: {
                            title: 'Is Target',
                            type: 'boolean',
                            default: false,
                            description: 'If this page is target for overflow',
                        },
                    },
                },
            ],
        },
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        'Layout Settings': [
            'pageName',
            'pageSize',
            'pageLayout',
            'pageWidth',
            'pageHeight',
            'leftColumnOptions',
            'rightColumnOptions',
            'pageOrientation',
            'pageMargin',
            'pagePadding',
            'pageBleed',
        ],
        'Overflow Settings': ['overflowOption', 'chainFlowName', 'isSource', 'isTarget'],
        'Header-Footer Opts': ['includeHeader', 'includeFooter'],
    },
];

const PageWidget: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        pageName: { 'ui:widget': 'CustomTextInput' },
        pageSize: { 'ui:widget': 'CustomSelect' },
        pageLayout: { 'ui:widget': 'CustomSelect' },
        pageWidth: { 'ui:widget': 'InchesInput' },
        pageHeight: { 'ui:widget': 'InchesInput' },
        pageOrientation: { 'ui:widget': 'Orientation' },
        pageMargin: { 'ui:field': 'PdfMarginField' },
        pagePadding: { 'ui:field': 'PdfMarginField' },
        pageBleed: { 'ui:field': 'PdfMarginField' },
        includeHeader: { 'ui:widget': 'CustomCheckbox' },
        includeFooter: { 'ui:widget': 'CustomCheckbox' },
        overflowOption: { 'ui:widget': 'CustomSelect' },
        isSource: { 'ui:widget': 'CustomCheckbox' },
        isTarget: { 'ui:widget': 'CustomCheckbox' },
        chainFlowName: { 'ui:widget': 'CustomTextInput' },
    },
    templateView: Page,
    id: 'Page',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: (context?: Record<string, unknown>) => {
        const inheritedProps: Record<string, unknown> = {};

        // Inherit page orientation and size from root document
        if (context?.rootDocumentProperties) {
            const rootProps = context.rootDocumentProperties as Record<string, unknown>;

            // Inherit pageOrientation (PDF) or pptxPageOrientation (PPTX)
            if (rootProps.pageOrientation) {
                inheritedProps.pageOrientation = rootProps.pageOrientation;
            } else if (rootProps.pptxPageOrientation) {
                inheritedProps.pageOrientation = rootProps.pptxPageOrientation;
            }

            // Inherit pageSize (PDF) or pptxPageSize (PPTX)
            if (rootProps.pageSize) {
                inheritedProps.pageSize = rootProps.pageSize;
            } else if (rootProps.pptxPageSize) {
                inheritedProps.pageSize = rootProps.pptxPageSize;
            }

            // Inherit pageWidth and pageHeight for custom sizes (PDF or PPTX)
            if (rootProps.pageWidth) {
                inheritedProps.pageWidth = rootProps.pageWidth;
            } else if (rootProps.pptxPageWidth) {
                inheritedProps.pageWidth = rootProps.pptxPageWidth;
            }

            if (rootProps.pageHeight) {
                inheritedProps.pageHeight = rootProps.pageHeight;
            } else if (rootProps.pptxPageHeight) {
                inheritedProps.pageHeight = rootProps.pptxPageHeight;
            }
        }

        return inheritedProps;
    },
};

export default PageWidget;
